import { RenderElementProps } from 'slate-react'

import type { MentionElement as MentionElementType } from '@/features/view-tfl-uploaded/types/slate'
import { useCurrentUser } from '@/hooks/useAuth'
import { cn } from '@/utils'
import { formatFullName } from '@/utils/common'

interface MentionItemBaseProps {
  userName: string
  userId: string
  userEmail?: string
}

interface MentionItemForMarkdownProps extends MentionItemBaseProps {
  attributes?: never
  children?: never
}

interface MentionItemForSlateProps extends RenderElementProps {
  element: MentionElementType
  userName?: never
  userId?: never
  userEmail?: never
}

type MentionItemProps = MentionItemForMarkdownProps | MentionItemForSlateProps

export const MentionItem: React.FC<MentionItemProps> = (props) => {
  const { data: currentUser } = useCurrentUser()

  // Determine if this is for Slate (has element) or Markdown (has userName/userId)
  const isSlateElement = 'element' in props

  let displayName: string
  let userId: string
  let userEmail: string | undefined
  let attributes: RenderElementProps['attributes'] | undefined
  let children: React.ReactNode | undefined

  if (isSlateElement) {
    const { element, attributes: attrs, children: ch } = props
    displayName = element.userName || formatFullName('', '')
    userId = element.userId
    userEmail = element.userEmail
    attributes = attrs
    children = ch
  } else {
    const { userName, userId: uid, userEmail: email } = props
    displayName = userName
    userId = uid
    userEmail = email
  }

  // Check if this mention is for the current logged user
  const isCurrentUser = currentUser?.id === userId

  return (
    <span
      {...attributes}
      contentEditable={isSlateElement ? false : undefined}
      className={cn(
        'inline-flex items-center px-1.5 mx-0.5 my-px rounded',
        'font-medium cursor-default whitespace-nowrap',
        isCurrentUser
          ? 'bg-blue-600 text-white'
          : 'bg-blue-100 text-primary-900'
      )}
      data-user-id={userId}
      data-user-email={userEmail}
    >
      @{displayName}
      {children}
    </span>
  )
}
